import React, { useEffect, useState } from 'react';
import Palette from './Palette';
import FlowCanvas from './FlowCanvas';
import Inspector from './Inspector';
import ResultsPanel from './ResultsPanel';
import { useWorkflowEditor } from './useWorkflowEditor';
import { STATUS_LABELS, isRunning, resultOf } from './graphModel';

// The workflow editor: a canvas of connected nodes, a palette to add them, an
// inspector for the selected one and the results of the last run. Everything
// is described by the node catalog the server exposes.
export default function WorkflowEditor({
  workflow,
  metas,
  onPersist,
  onRename,
  onRun,
  onDelete,
  onExport
}) {
  const editor = useWorkflowEditor({ workflow, metas, onPersist });
  const [title, setTitle] = useState(workflow.title);

  useEffect(() => setTitle(workflow.title), [workflow.id, workflow.title]);

  const running = isRunning(workflow.status);
  const context = {
    ...editor.context,
    fields: editor.selectedId ? editor.fieldsFor(editor.selectedId) : []
  };
  const results = (workflow.result && workflow.result.nodes) || {};

  // Read the persisted input data from the selected node's result entry.
  const inputPreview = (() => {
    if (!editor.selectedId) return null;
    const nodeResult = resultOf(results, editor.selectedId);
    if (!nodeResult || !nodeResult.input) return null;
    const input = nodeResult.input;
    return { fields: input.fields || [], rows: input.rows || [] };
  })();

  return (
    <div className="flow-editor">
      <header className="flow-topbar">
        <input
          className="flow-title"
          value={title}
          aria-label="Workflow title"
          onChange={(e) => setTitle(e.target.value)}
          onBlur={() => title !== workflow.title && onRename(title)}
          onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
        />
        <span className={`flow-status flow-status-${workflow.status}`}>
          {STATUS_LABELS[workflow.status] || workflow.status}
        </span>
        <button type="button" className="btn btn-primary" onClick={onRun} disabled={running}>
          {running ? 'Running…' : 'Run'}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onDelete}>
          Delete
        </button>
      </header>
      <div className="flow-body">
        <Palette metas={Object.values(metas)} onAdd={editor.addNodeType} />
        <div className="flow-workspace">
          <FlowCanvas
            graph={editor.graph}
            metas={metas}
            results={results}
            selectedId={editor.selectedId}
            onSelectNode={editor.setSelectedId}
            onChange={editor.changeGraph}
            workflow={workflow}
          />
          <Inspector
            node={editor.selectedNode}
            meta={editor.selectedMeta}
            context={context}
            inputPreview={inputPreview}
            onParamsChange={(params) => editor.changeParams(editor.selectedId, params)}
            onDelete={() => editor.deleteNode(editor.selectedId)}
          />
        </div>
      </div>
    </div>
  );
}
